/**
 * theologicalCenteringEngine.js
 *
 * Detects when Kevia should gently re-center the conversation toward a
 * theological anchor (grace, hope, accompaniment) — without being preachy.
 *
 * DISTINCT from denominationalReasoningEngine.js (which handles EGW/Manual
 * retrieval). This engine only shapes the *prompt posture* — no retrieval.
 *
 * Centers:
 *   gracia          — unconditional grace; for shame, spiritual confusion
 *   esperanza       — sober hope; for discouragement, fear, loss of meaning
 *   acompañamiento  — presence over solutions; for crisis, grief
 *   sabiduría       — practical pastoral wisdom; for conflict, decisions
 */

const CENTERING_INSTRUCTIONS = {
  gracia:
    "Si conviene, recentra suavemente en la gracia de Dios. Sin resolver la tensión prematuramente.",
  esperanza:
    "Puedes anclar en esperanza bíblica — sobria, arraigada, no triunfalista.",
  acompañamiento:
    "No espiritualices el dolor demasiado rápido. Primero acompaña; el mensaje llega después.",
  sabiduría:
    "Puedes ofrecer sabiduría práctica pastoral, no solo análisis.",
};

/**
 * @param {{
 *   emotion:   object|null,
 *   intent:    string,
 *   mood:      string,
 *   riskLevel: string,
 * }} params
 * @returns {{ needsCentering: boolean, centerOn: string|null, instruction: string|null }}
 */
export function detectTheologicalCentering({ emotion, intent, mood, riskLevel }) {
  // Shame or spiritual confusion → center on grace
  if (
    emotion?.dominantEmotion === "shame" ||
    emotion?.dominantEmotion === "spiritual_confusion"
  ) {
    return {
      needsCentering: true,
      centerOn:       "gracia",
      instruction:    CENTERING_INSTRUCTIONS.gracia,
    };
  }

  // Discouragement, loneliness, loss of meaning → center on hope
  if (["discouragement", "loneliness", "fear"].includes(emotion?.dominantEmotion)) {
    return {
      needsCentering: true,
      centerOn:       "esperanza",
      instruction:    CENTERING_INSTRUCTIONS.esperanza,
    };
  }

  // Critical / high risk → don't theologize too fast, center on accompaniment
  if (riskLevel === "critical" || riskLevel === "high") {
    return {
      needsCentering: true,
      centerOn:       "acompañamiento",
      instruction:    CENTERING_INSTRUCTIONS.acompañamiento,
    };
  }

  // Conflict in pastoral setting → practical pastoral wisdom
  if (
    emotion?.dominantEmotion === "conflict" &&
    (intent === "family_followup" || intent === "visit_plan")
  ) {
    return {
      needsCentering: true,
      centerOn:       "sabiduría",
      instruction:    CENTERING_INSTRUCTIONS.sabiduría,
    };
  }

  return { needsCentering: false, centerOn: null, instruction: null };
}
